// Chart tokens for the plain SVG / CSS charts (charts/*.js) and the
// performance cards. SVG fills and strokes cannot read Tailwind classes, so
// the V2 `bm` palette is repeated here as hex values; nothing else may be
// used. Lighter oranges are bm-orange at an opacity only (ORANGE_ALPHA),
// never a new orange hex.
//
// Colour roles (spec B18, from Jayson's mocks):
//   this player / squad, team wins  -> ROLE.self / ROLE.win (bm-orange)
//   opponents, peers, losses        -> ROLE.opponent / ROLE.peer / ROLE.loss
//   medians, averages, trend lines  -> ROLE.median / ROLE.average / ROLE.trend (bm-ink)
//   better / worse                  -> ROLE.better / ROLE.worse
//   scoring mix                     -> MIX (2PT ink, 3PT orange, FT orange at 40%)
//   player archetypes               -> archetypeColor(name, payloadColor), data
//                                      colours with a 1px ring, never UI chrome.

export const CHART = {
  ink: "#2c2c2c",
  muted: "#757575",
  tertiary: "#b8b8bc",
  border: "#e8e8ec",
  subtle: "#f7f7f8",
  mutedbg: "#f2f2f4",
  strong: "#d9d9d9",
  orange: "#FF4400",
  critical: "#e2241a",
  attention: "#edd200",
  positive: "#1f9d55",
  info: "#2f6fed",
  white: "#ffffff",
};

// bm-orange at the opacities V2 allows (/40, /20, /10, /5).
export const ORANGE_ALPHA = {
  40: "rgba(255, 68, 0, 0.4)",
  20: "rgba(255, 68, 0, 0.2)",
  10: "rgba(255, 68, 0, 0.1)",
  5: "rgba(255, 68, 0, 0.05)",
};

// Rings, spokes and gridlines.
export const GRID = CHART.border;
export const GRID_FAINT = CHART.mutedbg;

export const ROLE = {
  self: CHART.orange,
  win: CHART.orange,
  loss: CHART.tertiary,
  opponent: CHART.muted,
  peer: CHART.tertiary,
  median: CHART.ink,
  average: CHART.ink,
  trend: CHART.ink,
  better: CHART.positive,
  worse: CHART.critical,
};

// Scoring mix segments (one scheme on both pages): fill and label colour.
export const MIX = {
  two: { color: CHART.ink, text: CHART.white },
  three: { color: CHART.orange, text: CHART.white },
  ft: { color: ORANGE_ALPHA[40], text: CHART.ink },
};

// Payload mix keys ("pts_2", "pts_2_share", "pts_3", "pts_ft_share", ...)
// to their segment colours; anything else reads as the 2PT ink.
export const mixColors = (key) => {
  const k = String(key || "").toLowerCase();
  if (/ft/.test(k)) return MIX.ft;
  if (/3|three/.test(k)) return MIX.three;
  return MIX.two;
};

// Insight severities (payload `severity`) as dot colours and Tailwind
// classes.
export const SEVERITY_COLORS = {
  high: CHART.critical,
  medium: CHART.attention,
  info: CHART.info,
  good: CHART.positive,
};
export const SEVERITY_CLASSES = {
  high: "bg-bm-critical",
  medium: "bg-bm-attention",
  info: "bg-bm-info",
  good: "bg-bm-positive",
};
export const severityClass = (severity) => SEVERITY_CLASSES[severity] || "bg-bm-tertiary";

// Percentile bands (perfFormat.bandOf) as dot colours and classes.
export const BAND_COLORS = {
  positive: CHART.positive,
  attention: CHART.attention,
  critical: CHART.critical,
};
export const BAND_CLASSES = {
  positive: "bg-bm-positive",
  attention: "bg-bm-attention",
  critical: "bg-bm-critical",
};

// Player archetype colours: the scoring model's base colours, with the
// three that are hard to read on white replaced (B18). "Elite X" keeps X's
// colour. A payload colour is used for a label this map does not know.
export const ARCHETYPE_BASE_COLORS = {
  "All-Around Star": "#E0B400",
  "Two-Way": "#AA1EFB",
  Scorer: "#4437FD",
  "Defensive Specialist": "#EA3922",
  "Three & D": "#21BD4A",
  "Floor General": "#FA8314",
  "Perimeter Threat": "#18B8C9",
  Balanced: "#8a8f96",
};
const ARCHETYPE_OVERRIDES = new Set(["All-Around Star", "Perimeter Threat", "Balanced"]);
export const ARCHETYPE_FALLBACK = "#8a8f96";

export const archetypeBase = (name) => String(name || "").replace(/^Elite\s+/, "").trim();

export const archetypeColor = (name, payloadColor) => {
  const base = archetypeBase(name);
  if (ARCHETYPE_OVERRIDES.has(base)) return ARCHETYPE_BASE_COLORS[base];
  return payloadColor || ARCHETYPE_BASE_COLORS[base] || ARCHETYPE_FALLBACK;
};

// SVG text: Archivo for numbers and codes, Inter for labels (the bm type
// classes cannot style SVG text reliably, so charts set the family).
export const FONT = {
  num: "Archivo, sans-serif",
  body: "Inter, sans-serif",
};

// Phone chart widths (B17): a phone card's content is 306px wide on the
// 390px Figma phone (24px page gutter, 18px card padding), the width the V2
// phone radars already use (PercentileRadar, TeamStyleRadar). A 306 wide
// viewBox draws 1:1 there, so a 10px label reads 10px (a 340 wide one
// shrank it to 9px).
export const PHONE_CHART_WIDTH = 306;
export const PHONE_RADAR_WIDTH = 306;
